import { StageDef } from '../automationMachineInterfaces'
import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import * as selectAudienceUtils from './fbSelectAudienceModalUtils'
import {
  getSettingToggleValue,
  getSettingRowWithToggle,
  setSettingToggleValue,
  selectTargetSettinginDialog,
  openClickableSettingModal,
  getSettingModalSelectedPrivacyLevel,
  closeSettingModal,
  setSettingModalPrivacyLevel,
  getClickableSettingRow,
} from './fbSettingsUtils'
import { followersAndPublicContentUrl } from './fbUrls'
import { FacebookSettingKey } from '../../recommendations/recommendationSettingsKeys'
import { FBRecommendationKey } from '../../utils/enums'
import * as enums from '../../utils/enums'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCantUndoFnFromKey,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { shouldUpdateSetting } from '../utils'

// Setting property functions
const TARGET_VALUES = {
  [FacebookSettingKey.WHO_CAN_FOLLOW]: selectAudienceUtils.FRIENDS,
  [FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS]: selectAudienceUtils.FRIENDS,
  [FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS]: selectAudienceUtils.FRIENDS,
  [FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS]:
    selectAudienceUtils.FRIENDS,
  [FacebookSettingKey.PUBLIC_POST_NOTIF]:
    selectAudienceUtils.FRIENDS_OF_FRIENDS,
  [FacebookSettingKey.PUBLIC_PROFILE_INFO]: selectAudienceUtils.FRIENDS,
  [FacebookSettingKey.RELEVANT_COMMENTS]: true,
  [FacebookSettingKey.OFF_FACEBOOK_PREVIEWS]: false,
}

const getAudienceCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => value || null)
const getToggleCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? 'On' : 'Off'))

const currentValueFns = {
  [FacebookSettingKey.WHO_CAN_FOLLOW]: getAudienceCurrentValueFn(
    FacebookSettingKey.WHO_CAN_FOLLOW,
  ),
  [FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS]: getAudienceCurrentValueFn(
    FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS,
  ),
  [FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS]: getAudienceCurrentValueFn(
    FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS,
  ),
  [FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS]: getAudienceCurrentValueFn(
    FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS,
  ),
  [FacebookSettingKey.PUBLIC_POST_NOTIF]: getAudienceCurrentValueFn(
    FacebookSettingKey.PUBLIC_POST_NOTIF,
  ),
  [FacebookSettingKey.PUBLIC_PROFILE_INFO]: getAudienceCurrentValueFn(
    FacebookSettingKey.PUBLIC_PROFILE_INFO,
  ),
  [FacebookSettingKey.RELEVANT_COMMENTS]: getToggleCurrentValueFn(
    FacebookSettingKey.RELEVANT_COMMENTS,
  ),
  [FacebookSettingKey.OFF_FACEBOOK_PREVIEWS]: getToggleCurrentValueFn(
    FacebookSettingKey.OFF_FACEBOOK_PREVIEWS,
  ),
}

const getAudienceIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => {
    const currentSetting = {
      name: value,
      level: selectAudienceUtils.getPrivacySettingFromLabel(value),
    }
    return selectAudienceUtils.isPrivacySettingAsStrictOrStricter(
      currentSetting,
      TARGET_VALUES[key],
    )
  })
const getToggleIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => value === TARGET_VALUES[key])

const isFulfilledFns = {
  [FacebookSettingKey.WHO_CAN_FOLLOW]: getAudienceIsFulfilledFn(
    FacebookSettingKey.WHO_CAN_FOLLOW,
  ),
  [FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS]: getAudienceIsFulfilledFn(
    FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS,
  ),
  [FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS]: getAudienceIsFulfilledFn(
    FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS,
  ),
  [FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS]: getAudienceIsFulfilledFn(
    FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS,
  ),
  [FacebookSettingKey.PUBLIC_POST_NOTIF]: getAudienceIsFulfilledFn(
    FacebookSettingKey.PUBLIC_POST_NOTIF,
  ),
  [FacebookSettingKey.PUBLIC_PROFILE_INFO]: getAudienceIsFulfilledFn(
    FacebookSettingKey.PUBLIC_PROFILE_INFO,
  ),
  [FacebookSettingKey.RELEVANT_COMMENTS]: getToggleIsFulfilledFn(
    FacebookSettingKey.RELEVANT_COMMENTS,
  ),
  [FacebookSettingKey.OFF_FACEBOOK_PREVIEWS]: getToggleIsFulfilledFn(
    FacebookSettingKey.OFF_FACEBOOK_PREVIEWS,
  ),
}

const getCurrentValueFn = (key) => currentValueFns[key]
const getIsFulfilledFn = (key) => isFulfilledFns[key]

const getAudienceCantUndoFn = (key) =>
  getCantUndoFnFromKey(key, (value) =>
    value ? !selectAudienceUtils.isPrivacySettingUndoable(value) : false,
  )

const cantUndoFns = {
  [FacebookSettingKey.WHO_CAN_FOLLOW]: getAudienceCantUndoFn(
    FacebookSettingKey.WHO_CAN_FOLLOW,
  ),
  [FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS]: getAudienceCantUndoFn(
    FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS,
  ),
  [FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS]: getAudienceCantUndoFn(
    FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS,
  ),
  [FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS]: getAudienceCantUndoFn(
    FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS,
  ),
  [FacebookSettingKey.PUBLIC_POST_NOTIF]: getAudienceCantUndoFn(
    FacebookSettingKey.PUBLIC_POST_NOTIF,
  ),
  [FacebookSettingKey.PUBLIC_PROFILE_INFO]: getAudienceCantUndoFn(
    FacebookSettingKey.PUBLIC_PROFILE_INFO,
  ),
  [FacebookSettingKey.RELEVANT_COMMENTS]: defaultCantUndoFn,
  [FacebookSettingKey.OFF_FACEBOOK_PREVIEWS]: defaultCantUndoFn,
}

const getCantUndoFn = (key) => cantUndoFns[key]

const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const WHO_CAN_FOLLOW_LABEL = 'Who can follow me'
const WHO_CAN_SEE_FOLLOWERS_LABEL = 'Who can see your followers on your profile'
const WHO_CAN_SEE_YOUR_FOLLOWS_LABEL =
  'Who can see the people, Pages and lists you follow?'
const WHO_CAN_SEE_YOUR_FOLLOWS_LABEL_ALT =
  'Who can see the people and Pages you follow?'
const WHO_CAN_COMMENT_PUBLIC_POSTS_LABEL =
  'Who can comment on your public posts?'
const PUBLIC_POST_NOTIF_LABEL = 'Public post notifications'
const PUBLIC_PROFILE_INFO_LABEL = 'Public profile info'
const RELEVANT_COMMENTS_LABEL = 'Show most relevant comments first'
const OFF_FACEBOOK_PREVIEWS_LABEL = 'Off-Facebook previews'

// Gather stage

enum StageKey {
  GATHER_SETTING_FOLLOWERS_AND_FOLLOWS = 'GATHER_SETTING_FOLLOWERS_AND_FOLLOWS',
  GATHER_SETTING_PUBLIC_CONTENT = 'GATHER_SETTING_PUBLIC_CONTENT',
  UPDATE_SETTING_FOLLOWERS_AND_FOLLOWS = 'UPDATE_SETTING_FOLLOWERS_AND_FOLLOWS',
  UPDATE_SETTING_PUBLIC_CONTENT = 'UPDATE_SETTING_PUBLIC_CONTENT',
  UNDO_SETTING_FOLLOWERS_AND_FOLLOWS = 'UNDO_SETTING_FOLLOWERS_AND_FOLLOWS',
  UNDO_SETTING_PUBLIC_CONTENT = 'UNDO_SETTING_PUBLIC_CONTENT',
  FINISH_SETTING_FOLLOWERS_AND_PUBLIC_CONTENT = 'FINISH_SETTING_FOLLOWERS_AND_PUBLIC_CONTENT',
}

function chooseVisibleLabel(options: string[]): Promise<string> {
  return Promise.any(
    options.map((option) =>
      getClickableSettingRow([option]).then(() => option),
    ),
  ).catch(() => {
    throw new Error(
      `chooseVisibleLabel: None of the options were found on page {${options.join(', ')}}`,
    )
  })
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_FOLLOWERS_AND_FOLLOWS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: followersAndPublicContentUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      await openClickableSettingModal([WHO_CAN_FOLLOW_LABEL])
      const whoCanFollowValue =
        await getSettingModalSelectedPrivacyLevel(WHO_CAN_FOLLOW_LABEL)
      await closeSettingModal(WHO_CAN_FOLLOW_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      await openClickableSettingModal([WHO_CAN_SEE_FOLLOWERS_LABEL])
      const whoCanSeeFollowersValue = await getSettingModalSelectedPrivacyLevel(
        WHO_CAN_SEE_FOLLOWERS_LABEL,
      )
      await closeSettingModal(WHO_CAN_SEE_FOLLOWERS_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      const whoCanSeeYourFollowsLabel = await chooseVisibleLabel([
        WHO_CAN_SEE_YOUR_FOLLOWS_LABEL,
        WHO_CAN_SEE_YOUR_FOLLOWS_LABEL_ALT,
      ])
      await openClickableSettingModal([whoCanSeeYourFollowsLabel])
      const whoCanSeeFollowsValue = await getSettingModalSelectedPrivacyLevel(
        whoCanSeeYourFollowsLabel,
      )
      await closeSettingModal(whoCanSeeYourFollowsLabel, 'CANCEL', {
        removeModalManually: true,
      })

      updateStoredInitialValues(
        {
          [FacebookSettingKey.WHO_CAN_FOLLOW]: whoCanFollowValue.name,
          [FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS]:
            whoCanSeeFollowersValue.name,
          [FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS]:
            whoCanSeeFollowsValue.name,
        },
        FBRecommendationKey.FB_FOLLOWERS_PUBLIC_CONTENT,
      )
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_PUBLIC_CONTENT,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: followersAndPublicContentUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      await openClickableSettingModal([WHO_CAN_COMMENT_PUBLIC_POSTS_LABEL])
      const whoCanCommentValue = await getSettingModalSelectedPrivacyLevel(
        WHO_CAN_COMMENT_PUBLIC_POSTS_LABEL,
      )
      await closeSettingModal(WHO_CAN_COMMENT_PUBLIC_POSTS_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      await openClickableSettingModal([PUBLIC_POST_NOTIF_LABEL])
      const publicPostNotificationsValue =
        await getSettingModalSelectedPrivacyLevel(PUBLIC_POST_NOTIF_LABEL)
      await closeSettingModal(PUBLIC_POST_NOTIF_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      await openClickableSettingModal([PUBLIC_PROFILE_INFO_LABEL])
      const publicProfileValue = await getSettingModalSelectedPrivacyLevel(
        PUBLIC_PROFILE_INFO_LABEL,
      )
      await closeSettingModal(PUBLIC_PROFILE_INFO_LABEL, 'CANCEL', {
        removeModalManually: true,
      })

      const relevantCommentsRow = await getSettingRowWithToggle(
        RELEVANT_COMMENTS_LABEL,
      )
      const relevantCommentsOn =
        await getSettingToggleValue(relevantCommentsRow)

      const offFacebookPreviewsRow = await getSettingRowWithToggle(
        OFF_FACEBOOK_PREVIEWS_LABEL,
      )
      const offFacebookPreviewsOn = await getSettingToggleValue(
        offFacebookPreviewsRow,
      )

      updateStoredInitialValues(
        {
          [FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS]:
            whoCanCommentValue.name,
          [FacebookSettingKey.PUBLIC_POST_NOTIF]:
            publicPostNotificationsValue.name,
          [FacebookSettingKey.PUBLIC_PROFILE_INFO]: publicProfileValue.name,
          [FacebookSettingKey.RELEVANT_COMMENTS]: relevantCommentsOn,
          [FacebookSettingKey.OFF_FACEBOOK_PREVIEWS]: offFacebookPreviewsOn,
        },
        FBRecommendationKey.FB_FOLLOWERS_PUBLIC_CONTENT,
      )
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_FOLLOWERS_AND_FOLLOWS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: followersAndPublicContentUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_FOLLOW,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([WHO_CAN_FOLLOW_LABEL])
        await selectTargetSettinginDialog(
          TARGET_VALUES[FacebookSettingKey.WHO_CAN_FOLLOW].name,
          isDry,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([WHO_CAN_SEE_FOLLOWERS_LABEL])
        await setSettingModalPrivacyLevel(
          WHO_CAN_SEE_FOLLOWERS_LABEL,
          isDry,
          TARGET_VALUES[FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS],
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS,
          getIsFulfilledFn,
        )
      ) {
        const whoCanSeeYourFollowsLabel = await chooseVisibleLabel([
          WHO_CAN_SEE_YOUR_FOLLOWS_LABEL,
          WHO_CAN_SEE_YOUR_FOLLOWS_LABEL_ALT,
        ])
        await openClickableSettingModal([whoCanSeeYourFollowsLabel])
        await setSettingModalPrivacyLevel(
          whoCanSeeYourFollowsLabel,
          isDry,
          TARGET_VALUES[FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS],
        )
      }

      return true
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_PUBLIC_CONTENT,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: followersAndPublicContentUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([WHO_CAN_COMMENT_PUBLIC_POSTS_LABEL])

        await selectTargetSettinginDialog(
          TARGET_VALUES[FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS].name,
          isDry,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.PUBLIC_POST_NOTIF,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([PUBLIC_POST_NOTIF_LABEL])

        await selectTargetSettinginDialog(
          TARGET_VALUES[FacebookSettingKey.PUBLIC_POST_NOTIF].name,
          isDry,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.PUBLIC_PROFILE_INFO,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([PUBLIC_PROFILE_INFO_LABEL])

        await selectTargetSettinginDialog(
          TARGET_VALUES[FacebookSettingKey.PUBLIC_PROFILE_INFO].name,
          isDry,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.RELEVANT_COMMENTS,
          getIsFulfilledFn,
        )
      ) {
        const relevantCommentsRow = await getSettingRowWithToggle(
          RELEVANT_COMMENTS_LABEL,
        )
        const relevantCommentsOn =
          await getSettingToggleValue(relevantCommentsRow)
        if (!relevantCommentsOn && !isDry) {
          await setSettingToggleValue(relevantCommentsRow, true)
        }
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.OFF_FACEBOOK_PREVIEWS,
          getIsFulfilledFn,
        )
      ) {
        const offFacebookPreviewsRow = await getSettingRowWithToggle(
          OFF_FACEBOOK_PREVIEWS_LABEL,
        )
        const offFacebookPreviewsOn = await getSettingToggleValue(
          offFacebookPreviewsRow,
        )
        if (offFacebookPreviewsOn && !isDry) {
          await setSettingToggleValue(offFacebookPreviewsRow, false)
        }
      }

      return true
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.FINISH_SETTING_FOLLOWERS_AND_PUBLIC_CONTENT,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: followersAndPublicContentUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_FOLLOWERS_AND_FOLLOWS,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: followersAndPublicContentUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      const initialWhoCanFollow =
        initialValues[FacebookSettingKey.WHO_CAN_FOLLOW]
      const intialWhoCanSeeFollowers =
        initialValues[FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS]
      const initialWhoCanSeeFollows =
        initialValues[FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS]

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_FOLLOW,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([WHO_CAN_FOLLOW_LABEL])
        await selectTargetSettinginDialog(initialWhoCanFollow, isDry)
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_SEE_FOLLOWERS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([WHO_CAN_SEE_FOLLOWERS_LABEL])

        const targetPrivacySetting = {
          name: intialWhoCanSeeFollowers,
          level: selectAudienceUtils.getPrivacySettingFromLabel(
            intialWhoCanSeeFollowers,
          ),
        }

        await setSettingModalPrivacyLevel(
          WHO_CAN_SEE_FOLLOWERS_LABEL,
          isDry,
          targetPrivacySetting,
        )
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_SEE_YOUR_FOLLOWS,
          getIsFulfilledFn,
        )
      ) {
        const whoCanSeeYourFollowsLabel = await chooseVisibleLabel([
          WHO_CAN_SEE_YOUR_FOLLOWS_LABEL,
          WHO_CAN_SEE_YOUR_FOLLOWS_LABEL_ALT,
        ])
        await openClickableSettingModal([whoCanSeeYourFollowsLabel])
        const targetPrivacySetting = {
          name: initialWhoCanSeeFollows,
          level: selectAudienceUtils.getPrivacySettingFromLabel(
            initialWhoCanSeeFollows,
          ),
        }

        await setSettingModalPrivacyLevel(
          whoCanSeeYourFollowsLabel,
          isDry,
          targetPrivacySetting,
        )
      }

      return true
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_PUBLIC_CONTENT,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: followersAndPublicContentUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, initialValues, isDry } = userContext

      if (!settingsSelected) {
        return null
      }

      const initialWhoCanComment =
        initialValues[FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS]
      const initialPublicPostNotifications =
        initialValues[FacebookSettingKey.PUBLIC_POST_NOTIF]
      const initialPublicProfileInfo =
        initialValues[FacebookSettingKey.PUBLIC_PROFILE_INFO]
      const initalRelevantComments =
        initialValues[FacebookSettingKey.RELEVANT_COMMENTS]
      const initialOffFacebookPreviews =
        initialValues[FacebookSettingKey.OFF_FACEBOOK_PREVIEWS]

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.WHO_CAN_COMMENT_PUBLIC_POSTS,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([WHO_CAN_COMMENT_PUBLIC_POSTS_LABEL])

        await selectTargetSettinginDialog(initialWhoCanComment, isDry)
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.PUBLIC_POST_NOTIF,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([PUBLIC_POST_NOTIF_LABEL])

        await selectTargetSettinginDialog(initialPublicPostNotifications, isDry)
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.PUBLIC_PROFILE_INFO,
          getIsFulfilledFn,
        )
      ) {
        await openClickableSettingModal([PUBLIC_PROFILE_INFO_LABEL])

        await selectTargetSettinginDialog(initialPublicProfileInfo, isDry)
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.RELEVANT_COMMENTS,
          getIsFulfilledFn,
        )
      ) {
        const relevantCommentsRow = await getSettingRowWithToggle(
          RELEVANT_COMMENTS_LABEL,
        )
        const relevantCommentsOn =
          await getSettingToggleValue(relevantCommentsRow)
        if (relevantCommentsOn !== initalRelevantComments && !isDry) {
          await setSettingToggleValue(
            relevantCommentsRow,
            initalRelevantComments,
          )
        }
      }

      if (
        shouldUpdateSetting(
          settingsSelected,
          initialValues,
          FacebookSettingKey.OFF_FACEBOOK_PREVIEWS,
          getIsFulfilledFn,
        )
      ) {
        const offFacebookPreviewsRow = await getSettingRowWithToggle(
          OFF_FACEBOOK_PREVIEWS_LABEL,
        )
        const offFacebookPreviewsOn = await getSettingToggleValue(
          offFacebookPreviewsRow,
        )
        if (offFacebookPreviewsOn !== initialOffFacebookPreviews && !isDry) {
          await setSettingToggleValue(
            offFacebookPreviewsRow,
            initialOffFacebookPreviews,
          )
        }
      }

      return true
    },
  },
  {
    type: enums.StageType.ACTION,
    key: StageKey.FINISH_SETTING_FOLLOWERS_AND_PUBLIC_CONTENT,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: followersAndPublicContentUrl,
    action: async () => {
      window.location.reload()
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
